import { memo } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CBreadcrumb, CBreadcrumbItem } from "@coreui/react-pro";
import { routePaths } from "../../routes/route-paths";
import { useHelpArticles } from "../../pages/help/useHelpArticles";

interface Crumb {
  pathname: string;
  label: string;
  active: boolean;
}

function buildCrumbs(
  pathname: string,
  t: (k: string) => string,
  helpTitle: (slug: string) => string | null
): Crumb[] {
  if (pathname === "/" || pathname === routePaths.dashboard) {
    return [];
  }

  const segments = pathname.split("/").filter(Boolean);
  const crumbs: Crumb[] = [];
  let currentPath = "";

  for (let i = 0; i < segments.length; i++) {
    currentPath = currentPath + "/" + segments[i];
    const label = labelForSegment(segments, i, t, helpTitle);
    // A subscription's own pages live under /subscriptions, which has no page of its own: the list is My
    // strategies.
    const crumbPath =
      i === 0 && segments[0] === "subscriptions" ? routePaths.myStrategies : currentPath;
    if (label) {
      crumbs.push({
        pathname: crumbPath,
        label,
        active: i === segments.length - 1,
      });
    }
  }

  return crumbs;
}

function labelForSegment(
  segments: string[],
  index: number,
  t: (k: string) => string,
  helpTitle: (slug: string) => string | null
): string | null {
  const seg = segments[index];

  // Top-level routes
  if (index === 0) {
    switch (seg) {
      case "strategies":
        return t("common:navigation.strategies");
      case "my-strategies":
      case "subscriptions":
        return t("common:navigation.myStrategies");
      case "memberships":
        return t("common:navigation.memberships");
      case "add-ons":
        return t("common:navigation.addOns");
      case "portfolio-builder":
        return t("common:navigation.portfolioBuilder");
      case "signals":
        return t("common:navigation.signals");
      case "cart":
        return t("common:navigation.cart");
      case "checkout":
        return t("common:navigation.checkout");
      case "profile":
        return t("common:navigation.profile");
      case "signal-management":
        return t("common:navigation.signalManagement");
      case "help":
        return t("common:navigation.help");
      default:
        return null;
    }
  }

  // Help slugs are opaque in the URL, so read the article title from the
  // registry rather than title-casing the slug.
  if (segments[0] === "help" && index === 1) {
    return helpTitle(seg);
  }

  // Sub-routes
  if (segments[0] === "profile") {
    if (seg === "edit") return t("common:buttons.edit");
    if (seg === "email") return "Email";
    if (seg === "tradingview") return "TradingView username";
  }
  if (segments[0] === "subscriptions" && index === 1) {
    if (seg === "end") return t("common:breadcrumb.wizard");
    return t("common:breadcrumb.details");
  }
  if (segments[0] === "strategies" && index === 1) {
    return t("common:breadcrumb.details");
  }
  if (segments[0] === "my-strategies" && index === 1) {
    return t("common:breadcrumb.details");
  }
  if (segments[0] === "memberships" && index === 1) {
    if (seg === "request") return t("common:breadcrumb.request");
    return t("common:breadcrumb.details");
  }
  if (segments[0] === "add-ons" && index === 1) {
    return t("common:breadcrumb.details");
  }
  if (segments[0] === "signals" && index === 1) {
    if (seg === "configure") return t("common:breadcrumb.configure");
    if (seg === "portfolio-tracker") return t("common:navigation.portfolioTracker");
    return t("common:breadcrumb.details");
  }
  if (segments[0] === "signals" && segments[1] === "portfolio-tracker" && index === 2) {
    return t("common:breadcrumb.details");
  }
  if (segments[0] === "signals" && segments[1] === "configure" && index === 2) {
    if (seg === "change") return t("common:breadcrumb.change");
  }

  return null;
}

function AppBreadcrumbInner() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const helpArticles = useHelpArticles();
  const helpTitle = (slug: string) =>
    helpArticles.find((article) => article.slug === slug)?.title ?? null;
  const crumbs = buildCrumbs(pathname, t, helpTitle);

  return (
    <CBreadcrumb className="my-0">
      <CBreadcrumbItem>
        <Link to={routePaths.dashboard}>{t("common:navigation.home")}</Link>
      </CBreadcrumbItem>
      {crumbs.map((c, idx) =>
        c.active ? (
          <CBreadcrumbItem key={idx} active>
            {c.label}
          </CBreadcrumbItem>
        ) : (
          <CBreadcrumbItem key={idx}>
            <Link to={c.pathname}>{c.label}</Link>
          </CBreadcrumbItem>
        )
      )}
    </CBreadcrumb>
  );
}

export const AppBreadcrumb = memo(AppBreadcrumbInner);
